import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams, useHistory } from "react-router-dom";
import FinLogo from "../../../img/fin.png";
import { fetchOrderById, fetchOrderItemsByOrderId } from "../../../redux-toolkit/order/order-thunks";
import {
  selectOrder,
  selectOrderItems,
  selectIsOrderLoading,
} from "../../../redux-toolkit/order/order-selector";
import Spinner from "../../../components/Spinner/Spinner";
import "./InvoicePage.css";

const COMPANY = {
  name:    "Finllie",
  gstin:   "29AKEPR4195D1ZV",
  address: "No 44, 45, 46, Silicon Town Main Road, Doddanagamangala, Electronic City Phase 2, Bengaluru, Karnataka 560100, India",
  email:   "finlliesales@gmail.com",
  phone:   "+91 9945089397",
};

const GST_RATE = 0.05; // 5% total (CGST 2.5% + SGST 2.5%), MRP inclusive

const calcGST = (mrpTotal: number) => {
  const basePrice = mrpTotal / (1 + GST_RATE);
  const gstTotal  = mrpTotal - basePrice;
  const cgst      = gstTotal / 2;
  const sgst      = gstTotal / 2;
  return {
    basePrice: +basePrice.toFixed(2),
    gstTotal:  +gstTotal.toFixed(2),
    cgst:      +cgst.toFixed(2),
    sgst:      +sgst.toFixed(2),
  };
};

/** Resolves line amount: prefers item.amount if > 0, else falls back to price × qty */
const resolveAmount = (item: any): number => {
  const amt   = Number(item.amount ?? 0);
  const price = Number(item.perfume?.price ?? 0);
  const qty   = Number(item.quantity ?? 0);
  return amt > 0 ? amt : price * qty;
};

/**
 * Android WebView does not support window.print().
 * Fallback: render the invoice div as an HTML blob and open it in a new tab,
 * which triggers the browser's native share/print sheet on Android.
 */
const handlePrint = () => {
  const isAndroid = /android/i.test(navigator.userAgent);

  if (isAndroid) {
    const invoiceEl = document.getElementById("invoice-doc");
    if (!invoiceEl) return;

    // Collect all <style> and <link rel="stylesheet"> from the host page
    const styles = Array.from(document.querySelectorAll('style, link[rel="stylesheet"]'))
      .map((el) => el.outerHTML)
      .join("\n");

    const html = `
      <!DOCTYPE html>
      <html>
        <head>
          <meta charset="utf-8" />
          <meta name="viewport" content="width=device-width, initial-scale=1" />
          <title>Invoice</title>
          ${styles}
          <style>
            .no-print { display: none !important; }
            body { margin: 0; padding: 0; }
          </style>
        </head>
        <body>${invoiceEl.outerHTML}</body>
      </html>
    `;

    const blob = new Blob([html], { type: "text/html" });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement("a");
    a.href     = url;
    a.target   = "_blank";
    a.rel      = "noopener noreferrer";
    a.click();
    setTimeout(() => URL.revokeObjectURL(url), 5000);
  } else {
    window.print();
  }
};

const InvoicePage = () => {
  const dispatch = useDispatch();
  const history  = useHistory();
  const params   = useParams<{ id: string }>();

  const order   = useSelector(selectOrder);
  const items   = useSelector(selectOrderItems);
  const loading = useSelector(selectIsOrderLoading);

  useEffect(() => {
    dispatch(fetchOrderById(params.id));
    dispatch(fetchOrderItemsByOrderId(params.id));
  }, [dispatch, params.id]);

  // Grand total: prefer order.totalPrice if set, else sum resolved line amounts
  const grandTotal = order.totalPrice != null && Number(order.totalPrice) > 0
    ? Number(order.totalPrice)
    : items.reduce((s, item) => s + resolveAmount(item), 0);

  const { basePrice, cgst, sgst, gstTotal } = calcGST(grandTotal);

  if (loading) return <Spinner />;

  return (
    <div className="inv-shell">

      {/* ── ACTION BAR (hidden on print) ── */}
      <div className="inv-action-bar no-print">
        <button className="inv-back-btn" onClick={() => history.goBack()}>
          ← Back
        </button>
        <div className="inv-action-right">
          <span className="inv-action-hint">Invoice #{order.id}</span>
          <button className="inv-print-btn" onClick={handlePrint}>
            🖨 Download / Print PDF
          </button>
        </div>
      </div>

      {/* ── INVOICE DOCUMENT ── */}
      <div className="inv-doc" id="invoice-doc">

        {/* ── TOP HEADER ── */}
        <div className="inv-top">
          <div className="inv-brand">
            <img src={FinLogo} alt="Finllie" className="inv-brand-logo" />
            <div className="inv-brand-info">
              <div className="inv-brand-name">{COMPANY.name}</div>
              <div className="inv-brand-sub">{COMPANY.address}</div>
              <div className="inv-brand-sub">GSTIN: <strong>{COMPANY.gstin}</strong></div>
              <div className="inv-brand-sub">{COMPANY.email} · {COMPANY.phone}</div>
            </div>
          </div>

          <div className="inv-title-block">
            <div className="inv-title-text">TAX INVOICE</div>
            <div className="inv-meta-grid">
              <span className="inv-meta-label">Invoice No.</span>
              <span className="inv-meta-val">INV-{String(order.id).padStart(6, "0")}</span>
              <span className="inv-meta-label">Order ID</span>
              <span className="inv-meta-val">#{order.id}</span>
              <span className="inv-meta-label">Date</span>
              <span className="inv-meta-val">
                {order.createdAt
                  ? new Date(order.createdAt).toLocaleString("en-IN", {
                      timeZone: "Asia/Kolkata",
                      day: "2-digit", month: "short", year: "numeric",
                      hour: "2-digit", minute: "2-digit", hour12: false,
                    })
                  : order.date
                    ? new Date(order.date).toLocaleDateString("en-IN", {
                        timeZone: "Asia/Kolkata",
                        day: "2-digit", month: "short", year: "numeric",
                      })
                    : "—"}
              </span>
              <span className="inv-meta-label">Payment</span>
              <span className="inv-meta-val">{order.paymentType ?? "—"}</span>
            </div>
          </div>
        </div>

        <div className="inv-rule" />

        {/* ── BILL TO / SHIP TO / STATUS ── */}
        <div className="inv-parties">
          <div className="inv-party">
            <div className="inv-party-title">BILL TO</div>
            <div className="inv-party-name">
              {[order.firstName, order.lastName].filter(Boolean).join(" ") || "—"}
            </div>
            <div className="inv-party-line">{order.email}</div>
            <div className="inv-party-line">{order.phoneNumber}</div>
            {order.transactionId && (
              <div className="inv-party-line">Txn: {order.transactionId}</div>
            )}
          </div>

          <div className="inv-party">
            <div className="inv-party-title">SHIP TO</div>
            <div className="inv-party-name">
              {[order.firstName, order.lastName].filter(Boolean).join(" ") || "—"}
            </div>
            <div className="inv-party-line">{order.address || "—"}</div>
            <div className="inv-party-line">
              {[order.city, order.postIndex].filter(Boolean).join(" – ") || "—"}
            </div>
          </div>

          <div className="inv-party inv-party-status">
            <div className="inv-party-title">STATUS</div>
            <div className="inv-status-badge">{order.deliveryStatus ?? "—"}</div>
            {order.orderType && (
              <div className="inv-order-type">{order.orderType}</div>
            )}
          </div>
        </div>

        <div className="inv-rule" />

        {/* ── ITEMS TABLE ── */}
        <div className="inv-table-wrap">
          <table className="inv-table">
            <thead>
              <tr>
                <th className="inv-th inv-th-num">#</th>
                <th className="inv-th">Item Description</th>
                <th className="inv-th inv-th-hide-sm">Brand</th>
                <th className="inv-th inv-th-right">MRP (₹)</th>
                <th className="inv-th inv-th-right">Qty</th>
                <th className="inv-th inv-th-right">Amount (₹)</th>
              </tr>
            </thead>
            <tbody>
              {items.map((item, index) => (
                <tr key={item.id} className={index % 2 === 0 ? "inv-tr-even" : ""}>
                  <td className="inv-td inv-td-num">{index + 1}</td>
                  <td className="inv-td inv-td-name">{item.perfume?.perfumeTitle ?? "—"}</td>
                  <td className="inv-td inv-th-hide-sm">{item.perfume?.brand ?? "—"}</td>
                  <td className="inv-td inv-td-right">
                    {Number(item.perfume?.price ?? 0).toFixed(2)}
                  </td>
                  <td className="inv-td inv-td-right">{item.quantity}</td>
                  {/* ✅ Falls back to price × qty for DIST_REPLENISH orders where amount = 0 */}
                  <td className="inv-td inv-td-right inv-td-amount">
                    {resolveAmount(item).toFixed(2)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* ── TOTALS ── */}
        <div className="inv-totals-wrap">
          <div className="inv-totals">

            <div className="inv-total-row">
              <span>Taxable Amount <span className="inv-gst-note">(excl. GST)</span></span>
              <span>₹{basePrice.toFixed(2)}</span>
            </div>

            <div className="inv-total-row inv-gst-row">
              <span>
                CGST @ 2.5%
                <span className="inv-gst-note"> · GSTIN: {COMPANY.gstin}</span>
              </span>
              <span>₹{cgst.toFixed(2)}</span>
            </div>

            <div className="inv-total-row inv-gst-row">
              <span>SGST @ 2.5%</span>
              <span>₹{sgst.toFixed(2)}</span>
            </div>

            <div className="inv-total-row">
              <span>Shipping</span>
              <span className="inv-free">FREE</span>
            </div>

            <div className="inv-rule inv-rule-sm" />

            <div className="inv-total-row inv-grand">
              <div>
                <div>Grand Total</div>
                <div className="inv-gst-inclusive">
                  Inclusive of GST ₹{gstTotal.toFixed(2)} (CGST ₹{cgst.toFixed(2)} + SGST ₹{sgst.toFixed(2)})
                </div>
              </div>
              <span>₹{grandTotal.toFixed(2)}</span>
            </div>

          </div>
        </div>

        {/* ── FOOTER ── */}
        <div className="inv-rule" />
        <div className="inv-footer">
          <div className="inv-footer-left">
            <div className="inv-footer-note">
              This is a computer-generated invoice and does not require a physical signature.
            </div>
            <div className="inv-footer-note">
              For queries: {COMPANY.email}
            </div>
          </div>
        </div>

        <div className="inv-thankyou">
          Thank you for your business with Finllie! 🎉
        </div>
      </div>
    </div>
  );
};

export default InvoicePage;